// "Satır gidince odak Ekle'ye döner" — araç yüzeylerinin her birinin ayrı taşıdığı durum makinesi
// (GL #12). Satırı KALDIRAN bir eylem (izinden çıkar, sil, kapsamdan çıkar) satırı ve onay
// diyaloğunu açan menü öğesini DOM'dan söker; ikisi de odağı geri alamaz. Yenilenen listede satır
// yoksa ve diyalog kapalıysa odak kalıcı Ekle/Davet düğmesine iner — zamanlayıcıyla DEĞİL,
// efektle. Efektin Modal'ın kapanış dönüşünden SONRA çalışacağı garanti DEĞİLDİR: Radix
// `onCloseAutoFocus`'u diyalog söküldükten sonra 0 ms'lik bir zamanlayıcıdan çağırır, yani
// kapanışta satır zaten gitmişse bu efekt ÖNCE çalışır. Doğruluğu `Modal`'ın korumaları sağlar:
// dönüş hedefi DOM'dan kopmuşsa (`isConnected` / `document.contains`) Modal odağa dokunmaz,
// efektin verdiği odak kalır.
// Yenileme biter (başarı ya da hata) ve satır hâlâ duruyorsa talep düşer. Satırı TUTAN eylemler
// `removed`'ı asla çağırmaz; bir 404 satırın yenilemede kalkabileceği anlamına geliyorsa
// `removeFailed` çağırabilir (ör. giriş izni ver/geri al, davet iptali).

import { type RefObject, useEffect, useRef, useState } from 'react';

export interface RowRemovalFocusOptions {
  /** Satır kimliklerinin TAM listesi (aramayla süzülmüş alt küme DEĞİL). Efektin bağımlılığı kararlı olsun diye `useMemo(() => rows.map((r) => r.uuid), [rows])` ile türetin. */
  rowKeys: readonly string[];
  /** `rowKeys`'i taşıyan sorgunun zaman damgaları; değişmeleri yenilemenin bittiği anlamına gelir. */
  stamps: { dataUpdatedAt: number; errorUpdatedAt: number };
  /** Kalıcı Ekle/Davet düğmesi. */
  addButtonRef: RefObject<HTMLElement | null>;
  /** Onay diyaloğu hâlâ açık. */
  dialogOpen: boolean;
  /** Bir çekmece/modal açık: odağını asla çalma. */
  otherSurfaceOpen: boolean;
}

export interface RowRemovalFocus {
  /** Onay diyaloğunun `restoreFocusRef`'i olarak verilir. */
  menuRestoreRef: RefObject<HTMLElement | null>;
  /**
   * Menü öğesinin onSelect'inden, diyaloğu açmadan ÖNCE çağrılır. Kalan satırın dönüş hedefi ya üç
   * halkalı zincirdir — satırın ad bağlantısı/düğmesi → satırın kendi ⋮ tetikleyicisi → Ekle/Davet
   * düğmesi (son çare): `rememberOpener(nameRefs.get(id) ?? triggerRefs.get(id) ?? addButtonRef.current)`
   * — ya da satırla birlikte hep monte olan tek açıcıdır (araç yüzeyleri: `plateButtonRefs.get(id) ?? null`).
   */
  rememberOpener: (el: HTMLElement | null) => void;
  /** Satırı KALDIRAN eylemin (izinden çıkar, sil) mutasyon onSuccess'i: `menuRestoreRef`'i Ekle düğmesine çevirir ve efekti kurar. */
  removed: (rowKey: string) => void;
  /** Kaldıran eylemin — ya da 404'te satırı kaybedebilen, satırı tutan eylemin — mutasyon onError'ı (ör. 404 bayat satır): yalnız kurar; satır yenilemede kalkabilir. */
  removeFailed: (rowKey: string) => void;
}

export function useRowRemovalFocus({
  rowKeys,
  stamps,
  addButtonRef,
  dialogOpen,
  otherSurfaceOpen,
}: RowRemovalFocusOptions): RowRemovalFocus {
  // Bekleyen "odak Ekle'ye" talebi; damgalar, kaldırma anında sahip sorgunun durumudur.
  const [pending, setPending] = useState<{
    key: string;
    dataUpdatedAt: number;
    errorUpdatedAt: number;
  } | null>(null);
  const menuRestoreRef = useRef<HTMLElement | null>(null);

  // `addButtonRef` kararlı bir ref nesnesidir; bağımlılıklarda yalnız parametre olduğu için durur.
  useEffect(() => {
    if (pending === null || dialogOpen) {
      return;
    }
    // Başka bir yüzey açık: odağını çalma; talebi bırak.
    if (otherSurfaceOpen) {
      setPending(null);
      return;
    }
    if (!rowKeys.includes(pending.key)) {
      addButtonRef.current?.focus();
      setPending(null);
    } else if (
      stamps.dataUpdatedAt !== pending.dataUpdatedAt ||
      stamps.errorUpdatedAt !== pending.errorUpdatedAt
    ) {
      // Yenileme bitti (başarı ya da hata) ve satır duruyor: talep geçersiz.
      setPending(null);
    }
  }, [
    rowKeys,
    pending,
    dialogOpen,
    otherSurfaceOpen,
    stamps.dataUpdatedAt,
    stamps.errorUpdatedAt,
    addButtonRef,
  ]);

  function arm(rowKey: string) {
    setPending({
      key: rowKey,
      dataUpdatedAt: stamps.dataUpdatedAt,
      errorUpdatedAt: stamps.errorUpdatedAt,
    });
  }

  return {
    menuRestoreRef,
    rememberOpener: (el) => {
      menuRestoreRef.current = el;
    },
    removed: (rowKey) => {
      // Satır sunucuda gitti: diyaloğun kapanış dönüşü, yenileme ne zaman gelirse gelsin, satırın
      // bağlantısına DEĞİL kalıcı Ekle düğmesine iner.
      menuRestoreRef.current = addButtonRef.current;
      arm(rowKey);
    },
    removeFailed: arm,
  };
}
